import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { changePasswordRequest } from '@kreart/shared';
import { authStore } from '@/features/auth/store';
import { authApi } from '@/features/auth/api';
import { Button } from '@/components/ui/button';
import { Form, PasswordField, FormErrorBanner } from '@/components/form';

const formSchema = changePasswordRequest
  .and(z.object({ confirmPassword: z.string().min(1) }))
  .superRefine((val, ctx) => {
    if (val.newPassword !== val.confirmPassword) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['confirmPassword'],
        message: 'auth.passwordsMustMatch',
      });
    }
  });

type FormValues = z.infer<typeof formSchema>;

export function ChangePasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [pending, setPending] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });

  const onSubmit = async ({ currentPassword, newPassword }: FormValues) => {
    setSubmitError(null);
    setPending(true);
    try {
      await authApi.changePassword({ currentPassword, newPassword });
      // Backend revoked every refresh token — clear local state and go to login.
      authStore.setUnauthenticated();
      await navigate({ to: '/login', search: { changed: '1' } });
    } catch (err) {
      setSubmitError(err);
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="min-h-full flex items-center justify-center bg-muted/30 px-4 py-12">
      <div className="w-full max-w-sm rounded-lg border border-border bg-card p-8 shadow-sm">
        <h1 className="mb-2 text-center text-xl font-semibold text-foreground">
          {t('auth.mustChangeTitle')}
        </h1>
        <p className="mb-6 text-center text-sm text-muted-foreground">
          {t('auth.mustChangeDescription')}
        </p>
        <Form form={form} onSubmit={onSubmit} className="space-y-4">
          <PasswordField
            name="currentPassword"
            autoComplete="current-password"
            label={t('auth.currentPasswordLabel')}
          />
          <PasswordField
            name="newPassword"
            autoComplete="new-password"
            label={t('auth.newPasswordLabel')}
          />
          <PasswordField
            name="confirmPassword"
            autoComplete="new-password"
            label={t('auth.confirmPasswordLabel')}
          />
          <FormErrorBanner error={submitError} />
          <Button type="submit" className="w-full" disabled={pending}>
            {pending ? t('common.loading') : t('auth.changeSubmit')}
          </Button>
        </Form>
      </div>
    </div>
  );
}
